import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as UserManagementService from "../../services/UserManagement";

// Async thunks
export const fetchAllUsers = createAsyncThunk(
  "userManagement/fetchAllUsers",
  async (params, { rejectWithValue }) => {
    try {
      const response = await UserManagementService.getAllUsers(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch users" }
      );
    }
  }
);

export const fetchUserById = createAsyncThunk(
  "userManagement/fetchUserById",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await UserManagementService.getUserById(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch user" }
      );
    }
  }
);

export const createNewUser = createAsyncThunk(
  "userManagement/createNewUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await UserManagementService.createUser(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to create user" }
      );
    }
  }
);

export const updateUser = createAsyncThunk(
  "userManagement/updateUser",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const response = await UserManagementService.updateUserData(
        userId,
        userData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to update user" }
      );
    }
  }
);

export const removeUser = createAsyncThunk(
  "userManagement/removeUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await UserManagementService.deleteUser(userId);
      return { userId, ...response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to delete user" }
      );
    }
  }
);

export const fetchUserStats = createAsyncThunk(
  "userManagement/fetchUserStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserManagementService.getUserStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch user statistics" }
      );
    }
  }
);

export const toggleUserStatusAction = createAsyncThunk(
  "userManagement/toggleUserStatus",
  async ({ userId, isActive }, { rejectWithValue }) => {
    try {
      const response = await UserManagementService.toggleUserStatus(
        userId,
        isActive
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to toggle user status" }
      );
    }
  }
);

export const updateUserRoleAction = createAsyncThunk(
  "userManagement/updateUserRole",
  async ({ userId, role }, { rejectWithValue }) => {
    try {
      const response = await UserManagementService.updateUserRole(userId, role);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to update user role" }
      );
    }
  }
);

const initialState = {
  users: [],
  selectedUser: null,
  stats: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
  loading: false,
  statsLoading: false,
  error: null,
  successMessage: null,
};

const userManagementSlice = createSlice({
  name: "userManagement",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch users";
      })

      // Fetch user by ID
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload.user;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch user";
      })

      // Create new user
      .addCase(createNewUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.unshift(action.payload.user);
        state.successMessage = "User created successfully";
      })
      .addCase(createNewUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create user";
      })

      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(
          (user) => user.id === action.payload.user.id
        );
        if (index !== -1) {
          state.users[index] = action.payload.user;
        }
        if (state.selectedUser?.id === action.payload.user.id) {
          state.selectedUser = action.payload.user;
        }
        state.successMessage = "User updated successfully";
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update user";
      })

      // Delete user
      .addCase(removeUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(
          (user) => user.id !== action.payload.userId
        );
        state.successMessage = "User deleted successfully";
      })
      .addCase(removeUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete user";
      })

      // Fetch user stats
      .addCase(fetchUserStats.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload.stats;
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload?.message || "Failed to fetch statistics";
      })

      // Toggle user status
      .addCase(toggleUserStatusAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleUserStatusAction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(
          (user) => user.id === action.payload.user.id
        );
        if (index !== -1) {
          state.users[index] = action.payload.user;
        }
        state.successMessage = "User status updated successfully";
      })
      .addCase(toggleUserStatusAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update user status";
      })

      // Update user role
      .addCase(updateUserRoleAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserRoleAction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(
          (user) => user.id === action.payload.user.id
        );
        if (index !== -1) {
          state.users[index] = action.payload.user;
        }
        state.successMessage = "User role updated successfully";
      })
      .addCase(updateUserRoleAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update user role";
      });
  },
});

export const { clearError, clearSuccessMessage, clearSelectedUser } =
  userManagementSlice.actions;

export default userManagementSlice.reducer;
